import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";

import { formatDateTime } from "./admin/shared";

import type { ApiErrorCode } from "../api/client";
import type {
  AdminClientReport,
  AdminClientReportSource,
} from "@canvaschool/contracts";

const SOURCE_LABEL: Record<AdminClientReportSource, string> = {
  ERROR: "오류",
  USER_REPORT: "의견·제보",
  SURVEY: "설문",
  DIAGNOSTIC: "진단",
};

const ROLE_LABEL = { ADMIN: "관리자", TEACHER: "교사", STUDENT: "학생" };

const FOLD_AT = 120;

/** "Chrome 130 · Windows" style summary of a long user-agent string. */
export const shortUserAgent = (userAgent: string | null): string => {
  if (!userAgent) {
    return "—";
  }
  const browser =
    /Edg\/(\d+)/.exec(userAgent)?.[0].replace("Edg/", "Edge ") ??
    /Firefox\/(\d+)/.exec(userAgent)?.[0].replace("/", " ") ??
    /Chrome\/(\d+)/.exec(userAgent)?.[0].replace("/", " ") ??
    (/Version\/(\d+).*Safari/.test(userAgent)
      ? `Safari ${/Version\/(\d+)/.exec(userAgent)?.[1]}`
      : null);
  const system = /Windows/.test(userAgent)
    ? "Windows"
    : /Android/.test(userAgent)
    ? "Android"
    : /iPhone|iPad/.test(userAgent)
    ? "iOS"
    : /Mac OS X/.test(userAgent)
    ? "macOS"
    : /CrOS/.test(userAgent)
    ? "ChromeOS"
    : /Linux/.test(userAgent)
    ? "Linux"
    : null;
  const short = [browser, system].filter(Boolean).join(" · ");
  return short || userAgent.slice(0, 40);
};

const Message = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  if (text.length <= FOLD_AT) {
    return <>{text}</>;
  }
  return (
    <>
      {open ? text : `${text.slice(0, FOLD_AT)}…`}{" "}
      <button
        type="button"
        className="canvaschool-link-button"
        onClick={() => setOpen(!open)}
      >
        {open ? "접기" : "펼치기"}
      </button>
    </>
  );
};

/** A report a signed-in person wrote can be answered; they see it under the
 * header bell and in 나의 문의 이력. */
const answerable = (report: AdminClientReport) =>
  (report.source === "USER_REPORT" || report.source === "SURVEY") &&
  report.userLoginId !== null;

const ReplyCell = ({
  report,
  onReplied,
}: {
  report: AdminClientReport;
  onReplied: (reply: string, repliedAt: string) => void;
}) => {
  const auth = useAuth();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(report.reply ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiErrorCode | null>(null);

  if (!answerable(report)) {
    return <>—</>;
  }
  if (!editing) {
    return (
      <>
        {report.reply && (
          <p className="canvaschool-report-reply">
            <Message text={report.reply} />
            {report.repliedAt && (
              <span className="canvaschool-hint">
                {" "}
                ({formatDateTime(report.repliedAt)})
              </span>
            )}
          </p>
        )}
        <button
          type="button"
          className="canvaschool-link-button"
          onClick={() => setEditing(true)}
        >
          {report.reply ? "답변 고치기" : "답변하기"}
        </button>
      </>
    );
  }
  const submit = async () => {
    const message = text.trim();
    if (!message) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await auth.api.replyToClientReport(report.id, message);
      onReplied(message, new Date().toISOString());
      setEditing(false);
    } catch (failure) {
      setError(auth.handleError(failure));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="canvaschool-report-reply-form">
      <textarea
        aria-label="답변"
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={2000}
        rows={4}
      />
      <p className="canvaschool-hint">
        보낸 사람의 헤더 알림과 나의 문의 이력에 보입니다.
      </p>
      <div className="canvaschool-actions">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={pending || !text.trim()}
        >
          답변 보내기
        </button>
        <button
          type="button"
          className="canvaschool-btn-secondary"
          onClick={() => {
            setEditing(false);
            setText(report.reply ?? "");
          }}
          disabled={pending}
        >
          취소
        </button>
      </div>
      {error && <ErrorAlert code={error} />}
    </div>
  );
};

/** Error reports, user reports, survey answers and diagnostics, newest first. */
export const AdminReportsPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [reports, setReports] = useState<AdminClientReport[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [source, setSource] = useState<AdminClientReportSource | "">("");
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(
    async (cursor?: string) => {
      const ticket = ++requestRef.current;
      setError(null);
      setBusy(true);
      try {
        const result = await api.listClientReports({
          source: source || undefined,
          cursor,
        });
        if (ticket === requestRef.current) {
          setReports((current) =>
            cursor ? [...(current ?? []), ...result.items] : result.items,
          );
          setNextCursor(result.nextCursor);
        }
      } catch (failure) {
        if (ticket === requestRef.current) {
          setError(auth.handleError(failure));
        }
      } finally {
        if (ticket === requestRef.current) {
          setBusy(false);
        }
      }
    },
    [api, auth, source],
  );

  useEffect(() => {
    void load();
    // Reloading per filter; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, source]);

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>의견·제보</h1>
          <p className="canvaschool-page-lead">
            브라우저가 보낸 기록은 30일 동안 보관한 뒤 지웁니다.
          </p>
        </div>
      </div>
      <div className="canvaschool-inline-form">
        <label htmlFor="canvaschool-report-source">출처</label>
        <select
          id="canvaschool-report-source"
          value={source}
          onChange={(event) =>
            setSource(event.target.value as AdminClientReportSource | "")
          }
        >
          <option value="">전체</option>
          {(Object.keys(SOURCE_LABEL) as AdminClientReportSource[]).map(
            (value) => (
              <option key={value} value={value}>
                {SOURCE_LABEL[value]}
              </option>
            ),
          )}
        </select>
      </div>
      {error && <ErrorAlert code={error} />}
      {reports && reports.length === 0 && (
        <p className="canvaschool-hint">받은 제보가 없습니다.</p>
      )}
      {reports && reports.length > 0 && (
        <div className="canvaschool-table-wrap">
          <table className="canvaschool-table">
            <thead>
              <tr>
                <th scope="col">시각</th>
                <th scope="col">출처</th>
                <th scope="col">종류</th>
                <th scope="col">내용</th>
                <th scope="col">보낸 사람</th>
                <th scope="col">브라우저</th>
                <th scope="col">답변</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{formatDateTime(report.createdAt)}</td>
                  <td>{SOURCE_LABEL[report.source]}</td>
                  <td>{report.kind}</td>
                  <td>
                    <Message text={report.message} />
                  </td>
                  <td>
                    {report.userLoginId
                      ? `${report.userLoginId}${
                          report.userRole
                            ? ` (${ROLE_LABEL[report.userRole]})`
                            : ""
                        }`
                      : "로그인 전"}
                  </td>
                  <td title={report.userAgent ?? undefined}>
                    {shortUserAgent(report.userAgent)}
                  </td>
                  <td>
                    <ReplyCell
                      report={report}
                      onReplied={(reply, repliedAt) =>
                        setReports(
                          (current) =>
                            current?.map((item) =>
                              item.id === report.id
                                ? { ...item, reply, repliedAt }
                                : item,
                            ) ?? null,
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && (
        <button
          type="button"
          onClick={() => void load(nextCursor)}
          disabled={busy}
        >
          더 보기
        </button>
      )}
    </section>
  );
};
